import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  FiBarChart2,
  FiPieChart,
  FiActivity,
  FiMap,
  FiTrendingUp,
  FiDownload,
  FiRefreshCw
} from 'react-icons/fi';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart
} from 'recharts';

const Visualization = ({ data, files, theme = 'light' }) => {
  const [activeChart, setActiveChart] = useState('scatter');
  const [chartData, setChartData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const processChartData = useCallback(() => {
    setIsLoading(true);
    
    try {
      // Process events data for visualization
      const eventsData = data.events || [];
      const clustersData = data.clusters || [];
      const referencePoints = data.referencePoints || [];
      
      // Create scatter plot data
      const scatterData = eventsData.map(event => ({
        x: event.X,
        y: event.Y,
        amplitude: event.A,
        energy: event.E,
        counts: event.CNTS,
        id: event.Id,
        time: event.HHMMSS
      }));

      // Create amplitude distribution data
      const amplitudeRanges = [
        { range: '0-20', count: 0 },
        { range: '20-40', count: 0 },
        { range: '40-60', count: 0 },
        { range: '60-80', count: 0 },
        { range: '80-100', count: 0 },
        { range: '100+', count: 0 }
      ];

      eventsData.forEach(event => {
        const amp = event.A;
        if (amp < 20) amplitudeRanges[0].count++;
        else if (amp < 40) amplitudeRanges[1].count++;
        else if (amp < 60) amplitudeRanges[2].count++;
        else if (amp < 80) amplitudeRanges[3].count++;
        else if (amp < 100) amplitudeRanges[4].count++;
        else amplitudeRanges[5].count++;
      });

      // Create time series data
      const timeSeriesData = eventsData.map(event => ({
        time: event.HHMMSS,
        amplitude: event.A,
        counts: event.CNTS
      }));

      // Create cluster size distribution
      const clusterSizes = clustersData.map(cluster => ({
        name: `Cluster ${cluster.elements}`,
        size: cluster.elements,
        energy: cluster.sumEnergy,
        location: `${cluster.xLocMM}, ${cluster.yLocMM}`
      }));

      // Create reference point types
      const refPointTypes = {};
      referencePoints.forEach(point => {
        refPointTypes[point.type] = (refPointTypes[point.type] || 0) + 1;
      });

      const refPointData = Object.entries(refPointTypes).map(([type, count]) => ({
        name: type,
        value: count
      }));

      setChartData({
        scatter: scatterData,
        amplitude: amplitudeRanges,
        timeSeries: timeSeriesData,
        clusters: clusterSizes,
        referencePoints: refPointData
      });
    } catch (error) {
      console.error('Error processing chart data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [data]);

  useEffect(() => {
    if (data && data.events) {
      processChartData();
    }
  }, [data, processChartData]);

  const chartPalette = theme === 'dark'
    ? {
        grid: '#454545',
        axis: '#dbe5f0',
        accent: '#60a5fa',
        accentSoft: 'rgba(96, 165, 250, 0.3)',
        success: '#4ade80',
        pie: ['#60a5fa', '#4ade80', '#fbbf24', '#f87171', '#c084fc', '#f472b6']
      }
    : {
        grid: '#e5e7eb',
        axis: '#6b7280',
        accent: '#2563eb',
        accentSoft: 'rgba(37, 99, 235, 0.28)',
        success: '#16a34a',
        pie: ['#2563eb', '#16a34a', '#d97706', '#dc2626', '#7c3aed', '#db2777']
      };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="theme-chart-tooltip p-3">
          <p className="text-sm font-medium">
            {`X: ${label}`}
          </p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm">
              {`${entry.name}: ${entry.value}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const renderChart = () => {
    if (!chartData || isLoading) {
      return (
        <div className="flex items-center justify-center h-96">
          <div className="loading-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      );
    }

    switch (activeChart) {
      case 'scatter':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartPalette.grid} />
              <XAxis 
                type="number" 
                dataKey="x" 
                name="X Coordinate" 
                stroke={chartPalette.axis}
                label={{ value: 'X Coordinate', position: 'insideBottom', offset: -10 }}
              />
              <YAxis 
                type="number" 
                dataKey="y" 
                name="Y Coordinate" 
                stroke={chartPalette.axis}
                label={{ value: 'Y Coordinate', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Scatter 
                name="Events" 
                data={chartData.scatter} 
                fill={chartPalette.accent}
                fillOpacity={0.6}
              />
            </ScatterChart>
          </ResponsiveContainer>
        );

      case 'amplitude':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={chartData.amplitude} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartPalette.grid} />
              <XAxis dataKey="range" stroke={chartPalette.axis} />
              <YAxis stroke={chartPalette.axis} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" fill={chartPalette.accent} radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'timeSeries':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={chartData.timeSeries} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartPalette.grid} />
              <XAxis dataKey="time" stroke={chartPalette.axis} />
              <YAxis stroke={chartPalette.axis} />
              <Tooltip content={<CustomTooltip />} />
              <Area 
                type="monotone" 
                dataKey="amplitude" 
                stroke={chartPalette.accent}
                fill={chartPalette.accentSoft}
                fillOpacity={1}
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        );

      case 'clusters':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={chartData.clusters} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartPalette.grid} />
              <XAxis dataKey="name" stroke={chartPalette.axis} />
              <YAxis stroke={chartPalette.axis} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="size" fill={chartPalette.success} radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'referencePoints':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={chartData.referencePoints}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={120}
                fill="#8884d8"
                dataKey="value"
              >
                {chartData.referencePoints.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={chartPalette.pie[index % chartPalette.pie.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        );

      default:
        return <div>Select a chart type</div>;
    }
  };

  const chartTypes = [
    { id: 'scatter', name: 'Event Distribution', icon: FiMap },
    { id: 'amplitude', name: 'Amplitude Distribution', icon: FiBarChart2 },
    { id: 'timeSeries', name: 'Time Series', icon: FiActivity },
    { id: 'clusters', name: 'Cluster Analysis', icon: FiPieChart },
    { id: 'referencePoints', name: 'Reference Points', icon: FiTrendingUp }
  ];

  return (
    <div className="h-full">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-secondary-800 mb-2">Data Visualization</h2>
        <p className="text-secondary-600">Explore insights from your inspection data</p>
      </div>

      {/* Chart Type Selector */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-secondary-800">Chart Types</h3>
          <div className="flex space-x-2">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={processChartData}
              className="btn-outline flex items-center space-x-2"
            >
              <FiRefreshCw className={isLoading ? 'animate-spin' : ''} />
              <span>Refresh</span>
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn-outline flex items-center space-x-2"
            >
              <FiDownload />
              <span>Export</span>
            </motion.button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {chartTypes.map((type, index) => {
            const Icon = type.icon;
            const isActive = activeChart === type.id;
            
            return (
              <motion.button
                key={type.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setActiveChart(type.id)}
                className={`flex flex-col items-center p-3 rounded-lg border transition-all ${
                  isActive
                    ? 'bg-primary-50 border-primary-300 text-primary-700'
                    : 'bg-white border-secondary-200 hover:bg-secondary-50 text-secondary-700'
                }`}
              >
                <Icon className="w-5 h-5 mb-1" />
                <span className="text-xs font-medium">{type.name}</span>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-soft border border-secondary-100 p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary-500">Total Events</p>
              <p className="text-2xl font-bold text-secondary-800">
                {data?.events?.length || 0}
              </p>
            </div>
            <FiActivity className="w-8 h-8 text-primary-500" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-soft border border-secondary-100 p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary-500">Clusters</p>
              <p className="text-2xl font-bold text-secondary-800">
                {data?.clusters?.length || 0}
              </p>
            </div>
            <FiPieChart className="w-8 h-8 text-accent-500" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-soft border border-secondary-100 p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary-500">Reference Points</p>
              <p className="text-2xl font-bold text-secondary-800">
                {data?.referencePoints?.length || 0}
              </p>
            </div>
            <FiMap className="w-8 h-8 text-success-500" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-soft border border-secondary-100 p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary-500">Avg Amplitude</p>
              <p className="text-2xl font-bold text-secondary-800">
                {data?.events?.length 
                  ? Math.round(data.events.reduce((sum, e) => sum + e.A, 0) / data.events.length)
                  : 0}
              </p>
            </div>
            <FiBarChart2 className="w-8 h-8 text-warning-500" />
          </div>
        </motion.div>
      </div>

      {/* Chart Display */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-white rounded-xl shadow-soft border border-secondary-100 p-6"
      >
        <h3 className="font-semibold text-secondary-800 mb-4">
          {chartTypes.find(t => t.id === activeChart)?.name || 'Chart'}
        </h3>
        {renderChart()}
      </motion.div>

      {/* Empty State */}
      {!data && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-soft border border-secondary-100 p-12 text-center"
        >
          <FiBarChart2 className="w-16 h-16 text-secondary-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-secondary-800 mb-2">No Data Available</h3>
          <p className="text-secondary-600 mb-6">
            Load inspection data to visualize insights and patterns
          </p>
        </motion.div>
      )}
    </div>
  );
};

export default Visualization;
